import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { RaiseService } from '../../../core/services/api/raise.service';
import { ErrorService } from '../../../core/services/error.service';
import { BlockUiService } from '../../../shared/services/blockUi/block-ui.service';
import { FormTemplate } from '../../../shared/models/question/form-template';
import moment from 'moment';
import { PopupOptions } from '../../../shared/services/popup/popup-options';
import { PopupService } from '../../../shared/services/popup/popup.service';
import { SubFormHandlerService } from '../../../shared/services/sub-form-handler/sub-form-handler.service';
import { isPlatformBrowser, Location } from '@angular/common';
import { normalizeDuplicateFormKeys } from '../../../shared/utils/form-key-normalization';

@Component({
    selector: 'app-pre-registration',
    templateUrl: './pre-registration.component.html',
    styleUrls: ['./pre-registration.component.scss'],
    standalone: false
})
export class PreRegistrationComponent implements OnInit {
  form: FormTemplate;
  ecfFormTemplate: FormTemplate; // Store template for ECF form to split data later
  issuerFormTemplate: FormTemplate;
  ecfFormTemplateId: number; // Store template ID from API for submission
  isLoading: boolean = true;
  email: string = '';
  viewOnly: boolean = false;
  overrideExisting: boolean = false;
  originalTemplateId: number | null = null;

    private readonly subFormItemMarker = '__active_subform_item__';

    private readonly prefixResolvedQuestionKeys: Set<string> = new Set();

    private pendingSubmission: {
        dynamicFormTemplateId: number;
        content: string;
        ecfContent: string;
        email: string;
        name: string | null;
        overrideRejected: boolean;
    } | null = null;

  constructor(
    private subFormHandler: SubFormHandlerService,
    private raiseService: RaiseService,
    private errorService: ErrorService,
    private blockUiService: BlockUiService,
    private router: Router,
    private popupService: PopupService,
    private location: Location,
    @Inject(PLATFORM_ID) private platform: Object,
  ) {}

  submissionToken: string | null = null;
  viewToken: string | null = null;

  ngOnInit() {
    const state = isPlatformBrowser(this.platform) ? history.state : null;
    if (state && state.submissionToken) {
      this.submissionToken = state.submissionToken;
    }
    if (state && state.viewToken) {
      this.viewToken = state.viewToken;
    }
    if (state && state.viewOnly) {
      this.viewOnly = true;
    }
    if (state && state.overrideExisting) {
      this.overrideExisting = true;
    }
    if (state && state.originalTemplateId != null) {
      this.originalTemplateId = state.originalTemplateId;
    }

    if (!this.submissionToken && !this.viewOnly) {
      this.router.navigate(['/raise']);
      return;
    }

    if (state && state.prefillData && (state.prefillData.content || state.prefillData.ecfContent)) {
      try {
        const prefill = state.prefillData;
        const parsedContent = (prefill.content || prefill.Content) ? JSON.parse(prefill.content || prefill.Content) : null;
        const parsedECFContent = (prefill.ecfContent || prefill.ECFContent) ? JSON.parse(prefill.ecfContent || prefill.ECFContent) : null;

        // Fetch templates first to have the structure
        this.getForms(parsedContent, parsedECFContent);
        this.email = state.prefillData.email || '';
      } catch (err) {
        if (this.overrideExisting && !this.viewOnly) {
          this.popupService.alert('common.unknownError');
          this.isLoading = false;
          this.router.navigate(['/raise']);
          return;
        }
        this.getForms();
      }
    } else {
      if (state && state.prefillData && state.prefillData.email) {
        this.email = state.prefillData.email;
      }
      if (this.overrideExisting && !this.viewOnly) {
        this.popupService.alert('common.unknownError');
        this.isLoading = false;
        this.router.navigate(['/raise']);
        return;
      }
      this.getForms();
    }
  }

    getForms(prefillIssuer: any = null, prefillECF: any = null): void {
        this.isLoading = true;

        /*
         * Existing submitted application (viewed or overridden):
         * Use the saved form snapshots instead of downloading the latest templates,
         * so previously submitted answers stay attached to their original keys.
         */
        if ((this.viewOnly || this.overrideExisting) && (prefillIssuer || prefillECF)) {
            const needsIssuerFallback = !prefillIssuer;
            const needsEcfLookup =
                !prefillECF || (this.originalTemplateId == null && !this.ecfFormTemplateId);

            if (
                this.overrideExisting &&
                !this.viewOnly &&
                (needsIssuerFallback || needsEcfLookup)
            ) {
                forkJoin({
                    issuerForm: needsIssuerFallback
                        ? this.raiseService.getUnregisteredIssuerPreregistrationForm()
                        : of(null),
                    ecfForm: needsEcfLookup
                        ? this.originalTemplateId != null
                            ? this.raiseService.getPublicDynamicFormTemplateById(
                                  this.originalTemplateId,
                              )
                            : this.raiseService.getUnregisteredIssuerECFForm()
                        : of(null),
                }).subscribe({
                    next: (res) => {
                        if (
                            (needsIssuerFallback && !res.issuerForm) ||
                            (needsEcfLookup && !res.ecfForm) ||
                            (needsEcfLookup &&
                                this.originalTemplateId == null &&
                                !res.ecfForm?.id)
                        ) {
                            this.popupService.alert('common.unknownError');
                            this.router.navigate(['/raise']);
                            this.isLoading = false;
                            return;
                        }

                        if (this.originalTemplateId == null && res.ecfForm?.id) {
                            this.ecfFormTemplateId = res.ecfForm.id;
                        }

                        this.getForms(
                            prefillIssuer ?? res.issuerForm,
                            prefillECF ?? res.ecfForm,
                        );
                    },
                    error: (err) => {
                        this.errorService.showError(err);
                        this.router.navigate(['/raise']);
                        this.isLoading = false;
                    },
                });

                return;
            }

            try {
                const issuerTemplate = prefillIssuer
                    ? this.parseTemplate(prefillIssuer)
                    : null;

                const ecfTemplate = prefillECF
                    ? this.parseTemplate(prefillECF)
                    : null;

                normalizeDuplicateFormKeys(issuerTemplate);
                normalizeDuplicateFormKeys(ecfTemplate);

                // Structural marking for both snapshots; subform template
                // refinement (loads) runs afterwards when available.
                this.prefixResolvedQuestionKeys.clear();
                this.markSubmittedSubFormItems(issuerTemplate);
                this.markSubmittedSubFormItems(ecfTemplate);

                const issuerLoads = (issuerTemplate
                    ? this.collectSubmissionSubFormLoads(issuerTemplate)
                    : []
                ).filter(
                    (l) => !this.prefixResolvedQuestionKeys.has(l.questionKey),
                );
                const ecfLoads = (ecfTemplate
                    ? this.collectSubmissionSubFormLoads(ecfTemplate)
                    : []
                ).filter(
                    (l) => !this.prefixResolvedQuestionKeys.has(l.questionKey),
                );

                const distinctSubFormIds = [
                    ...new Set(
                        [...issuerLoads, ...ecfLoads].map((l) => l.subFormId),
                    ),
                ];

                if (distinctSubFormIds.length === 0) {
                    this.finishViewOnlyForm(issuerTemplate, ecfTemplate);
                    return;
                }

                forkJoin(
                    distinctSubFormIds.map((id) =>
                        this.raiseService
                            .getPublicDynamicFormTemplateById(id)
                            .pipe(
                                map((template) => ({ id, template })),
                                catchError(() => of({ id, template: null })),
                            ),
                    ),
                ).subscribe({
                    next: (fetched) => {
                        const templateMap = new Map(
                            fetched.map((f) => [f.id, f.template]),
                        );

                        this.markSubFormLoads(issuerTemplate, issuerLoads, templateMap);
                        this.markSubFormLoads(ecfTemplate, ecfLoads, templateMap);

                        this.finishViewOnlyForm(issuerTemplate, ecfTemplate);
                    },
                    error: (err) => {
                        this.errorService.showError(err);
                        this.isLoading = false;
                    },
                });
            } catch (err) {
                this.errorService.showError(err);
                this.isLoading = false;
            }

            return;
        }

        /*
         * New application:
         * Continue downloading the latest published templates.
         */
        forkJoin({
            issuerForm:
                this.raiseService.getUnregisteredIssuerPreregistrationForm(),
            ecfForm:
                this.raiseService.getUnregisteredIssuerECFForm(),
        }).subscribe(
            (res) => {
                const issuerTemplate = this.parseTemplate(res.issuerForm);
                const ecfTemplate = this.parseTemplate(res.ecfForm);

                this.ecfFormTemplateId = res.ecfForm.id;

                this.issuerFormTemplate = JSON.parse(
                    JSON.stringify(issuerTemplate),
                );

                this.ecfFormTemplate = JSON.parse(
                    JSON.stringify(ecfTemplate),
                );

                if (prefillIssuer) {
                    this.applyPrefill(issuerTemplate, prefillIssuer);
                }

                if (prefillECF) {
                    this.applyPrefill(ecfTemplate, prefillECF);
                }

                this.mergeForms(ecfTemplate, issuerTemplate);
                this.completeFormLoading();
            },
            (err) => {
                this.errorService.showError(err);
                this.isLoading = false;
            },
        );
    }

    private completeFormLoading(): void {
        if (this.email && this.form) {
            this.form.formPanels?.forEach((panel) => {
                panel.items?.forEach((item) => {
                    if (
                        item.key === 'email' ||
                        item.key === 'Email' ||
                        item.key === 'emailAddress' ||
                        item.key === 'EmailAddress'
                    ) {
                        item.value = this.email;
                    }
                });
            });
        }

        this.isLoading = false;

        if (this.viewOnly) {
            setTimeout(() => {
                this.form?.formPanels?.forEach((panel) => {
                    panel.items?.forEach((item) => {
                        if (
                            item.controlType !== 'title' &&
                            item.controlType !== 'description'
                        ) {
                            item.readonly = true;
                        }
                    });
                });
            });
        }
    }

    private parseTemplate(form: any): FormTemplate {
        let template: any = null;

        if (form?.content) {
            template = typeof form.content === 'string' ? JSON.parse(form.content) : form.content;
        } else if (form?.Content) {
            template = typeof form.Content === 'string' ? JSON.parse(form.Content) : form.Content;
        } else {
            template = form;
        }

        return this.normalizeFormTemplate(template);
    }

    private normalizeFormTemplate(template: any): any {
        if (!template) return template;

        const normalized = {
            ...template,
            title: template.title ?? template.Title,
            description: template.description ?? template.Description,
            formPanels: template.formPanels ?? template.FormPanels ?? [],
        };

        normalized.formPanels = normalized.formPanels.map((panel: any) => ({
            ...panel,
            title: panel.title ?? panel.Title,
            description: panel.description ?? panel.Description,
            items: panel.items ?? panel.Items ?? [],
        }));

        normalized.formPanels.forEach((panel: any) => {
            panel.items = panel.items.map((item: any) => ({
                ...item,
                key: item.key ?? item.Key,
                label: item.label ?? item.Label,
                value: item.value ?? item.Value,
                type: item.type ?? item.Type,
                controlType: item.controlType ?? item.ControlType,
                class: item.class ?? item.Class,
                placeholder: item.placeholder ?? item.Placeholder,
                description: item.description ?? item.Description,
                hoverText: item.hoverText ?? item.HoverText,
                readonly: item.readonly ?? item.Readonly,
                required: item.required ?? item.Required,
                rules: item.rules ?? item.Rules,
                options: item.options ?? item.Options ?? [],
            }));

            panel.items.forEach((item: any) => {
                item.options = item.options.map((option: any) => ({
                    ...option,
                    key: option.key ?? option.Key,
                    value: option.value ?? option.Value,
                    subForm: option.subForm ?? option.SubForm,
                    hasSub: option.hasSub ?? option.HasSub,
                }));

                if (item.type === 'array' && typeof item.value === 'string') {
                    const trimmedValue = item.value.trim();

                    try {
                        const parsedValue = JSON.parse(trimmedValue);
                        item.value = Array.isArray(parsedValue) ? parsedValue : [];
                    } catch {
                        item.value = trimmedValue
                            .split(',')
                            .map((v: string) => v.trim())
                            .filter((v: string) => v.length > 0);
                    }
                }
            });
        });

        return normalized;
    }

    private applyPrefill(template: FormTemplate, prefill: any) {
        if (!template || !prefill) return;

        template.formPanels?.forEach((panel) => {
            panel.items?.forEach((item) => {
                const prefillItem = this.findItemInPrefill(prefill, item.key);

                if (prefillItem && prefillItem.value != null) {
                    let val = prefillItem.value;

                    if (
                        item.type === 'array' ||
                        Array.isArray(val) ||
                        item.controlType === 'checkbox' ||
                        item.controlType === 'selection-list'
                    ) {
                        if (
                            typeof val === 'string' &&
                            val.trim().startsWith('[') &&
                            val.trim().endsWith(']')
                        ) {
                            try {
                                val = JSON.parse(val);
                            } catch { }
                        }
                    }

                    if (
                        item.type === 'number' ||
                        item.controlType === 'currency' ||
                        item.controlType === 'percentage'
                    ) {
                        const parsed = parseFloat(val.toString());
                        if (!isNaN(parsed)) val = parsed;
                    } else if (item.controlType === 'radio-boolean') {
                        if (val === 'true' || val === 1) val = true;
                        else if (val === 'false' || val === 0) val = false;
                        else val = !!val;
                    } else if (
                        item.controlType === 'dropdown' ||
                        (item.controlType === 'selection-list' && !Array.isArray(val))
                    ) {
                        const parsed = parseFloat(val.toString());
                        if (!isNaN(parsed) && parsed.toString() === val.toString()) val = parsed;
                    }

                    item.value = val;
                }
            });
        });
    }

    private findItemInPrefill(prefill: any, key: string) {
        for (const panel of prefill.formPanels || []) {
            const item = panel.items?.find((i: any) => i.key === key);
            if (item) return item;
        }

        return null;
    }

    private mergeForms(main: FormTemplate, secondary: FormTemplate) {
        if (!main || !secondary) {
            this.form = main || secondary;
            return;
        }

        const mainPanels = (main.formPanels || []).map((panel: any) => ({
            ...panel,
            _sourceForm: 'ecf',
        }));

        const secondaryPanels = (secondary.formPanels || []).map((panel: any) => ({
            ...panel,
            _sourceForm: 'issuer',
        }));

        this.form = {
            ...main,
            formPanels: [...mainPanels, ...secondaryPanels],
        };
    }

    private getConsumedValueStrings(value: any): string[] {
        const values: string[] = [];

        const collect = (val: any) => {
            if (val == null) return;

            if (
                typeof val === 'string' ||
                typeof val === 'number' ||
                typeof val === 'boolean'
            ) {
                values.push(val.toString());
            } else if (Array.isArray(val)) {
                val.forEach(collect);
            } else if (typeof val === 'object') {
                const key = val.key ?? val.Key;
                const optionValue = val.value ?? val.Value;

                if (key != null) values.push(key.toString());
                if (optionValue != null && optionValue.toString() !== key?.toString()) {
                    values.push(optionValue.toString());
                }
            }
        };

        collect(value);
        return values;
    }

    private getConsumedSubFormOptions(item: any): any[] {
        if (!item?.options) return [];

        const consumedValues = this.getConsumedValueStrings(item.value);

        return item.options.filter(
            (option: any) =>
                option?.hasSub &&
                consumedValues.some(
                    (v: string) =>
                        v === option.key?.toString() ||
                        v === option.value?.toString(),
                ),
        );
    }

    private hasConsumedSubFormOption(item: any): boolean {
        return this.getConsumedSubFormOptions(item).length > 0;
    }

    private markSubmittedSubFormItems(targetForm: any): void {
        if (!targetForm?.formPanels) return;

        targetForm.formPanels.forEach((formPanel: any) => {
            const items = formPanel.items || [];

            const groups: { questionKey: string; subFormId: string; indices: number[] }[] = [];

            // Pass 1 — exact marking via submitted key prefix
            // "{triggerKey}__{selectedOptionKey}__{subItemKey}".
            items.forEach((item: any) => {
                if (item._isSubFormItem) return;

                this.getConsumedSubFormOptions(item).forEach((option: any) => {
                    const optionKey = option.key ?? option.value;
                    if (!optionKey || !item.key) return;

                    const prefix = `${item.key}__${optionKey}__`;
                    const indices: number[] = [];

                    items.forEach((candidate: any, idx: number) => {
                        if (
                            candidate.key &&
                            candidate.key !== item.key &&
                            candidate.key.startsWith(prefix)
                        ) {
                            indices.push(idx);
                        }
                    });

                    if (indices.length === 0) return;

                    groups.push({
                        questionKey: item.key,
                        subFormId: option.subForm?.id?.toString() ?? optionKey.toString(),
                        indices,
                    });
                });
            });

            groups.forEach((group: any) => {
                group.indices.forEach((idx: number) => {
                    const subItem = items[idx];
                    subItem._subFormItemMarker = this.subFormItemMarker;
                    subItem._isSubFormItem = true;
                    subItem._subFormQuestionKey = group.questionKey;
                    subItem._subFormId = group.subFormId;
                });

                group.indices.forEach((idx: number, i: number) => {
                    items[idx]._isFirstSubFormItem = i === 0;
                    items[idx]._isLastSubFormItem = i === group.indices.length - 1;
                });

                this.prefixResolvedQuestionKeys.add(group.questionKey);
            });

            // Pass 2 — run-length fallback for triggers whose subform items
            // are not prefixed in the submitted keys (legacy data).
            const resolvedQuestionKeys = new Set(
                groups.map((g: any) => g.questionKey),
            );

            items.forEach((item: any, index: number) => {
                if (item._isSubFormItem) return;
                if (resolvedQuestionKeys.has(item.key)) return;
                if (!this.hasConsumedSubFormOption(item)) return;

                const subFormItems: any[] = [];
                let nextIndex = index + 1;

                while (
                    nextIndex < items.length &&
                    !items[nextIndex]._isSubFormItem &&
                    items[nextIndex].controlType !== 'title' &&
                    items[nextIndex].controlType !== 'description' &&
                    !this.hasConsumedSubFormOption(items[nextIndex])
                ) {
                    items[nextIndex]._subFormItemMarker = this.subFormItemMarker;
                    items[nextIndex]._isSubFormItem = true;
                    subFormItems.push(items[nextIndex]);

                    nextIndex++;
                }

                subFormItems.forEach((subItem: any, subIndex: number) => {
                    subItem._isFirstSubFormItem = subIndex === 0;
                    subItem._isLastSubFormItem = subIndex === subFormItems.length - 1;
                });
            });
        });
    }

    private finishViewOnlyForm(issuerTemplate: any, ecfTemplate: any): void {
        if (issuerTemplate) {
            this.issuerFormTemplate = JSON.parse(
                JSON.stringify(issuerTemplate),
            );
        }

        if (ecfTemplate) {
            this.ecfFormTemplate = JSON.parse(
                JSON.stringify(ecfTemplate),
            );
        }

        this.mergeForms(ecfTemplate, issuerTemplate);
        this.completeFormLoading();
    }

    private collectSubmissionSubFormLoads(
        template: any,
    ): { questionKey: string; subFormId: number; isMulti: boolean }[] {
        if (!template?.formPanels) return [];

        const loads: { questionKey: string; subFormId: number; isMulti: boolean }[] = [];

        template.formPanels.forEach((panel: any) => {
            (panel.items || []).forEach((item: any) => {
                if (item._isSubFormItem) return;

                const itemValue = item.value?.toString();
                if (!itemValue) return;

                let selectedValues: string[] = [];
                if (item.controlType === 'selection-list') {
                    if (Array.isArray(item.value)) {
                        selectedValues = item.value.map((v: any) => v?.toString());
                    } else {
                        try {
                            selectedValues = JSON.parse(itemValue);
                        } catch {
                            selectedValues = itemValue
                                .split(',')
                                .map((v: string) => v.trim());
                        }
                    }
                } else {
                    selectedValues = [itemValue];
                }

                const matchedOptions = (item.options || []).filter(
                    (opt: any) =>
                        opt?.hasSub &&
                        opt?.subForm?.id &&
                        selectedValues.some(
                            (v: string) =>
                                v === opt.key?.toString() ||
                                v === opt.value?.toString(),
                        ),
                );

                matchedOptions.forEach((opt: any) => {
                    if (
                        !loads.some(
                            (l) =>
                                l.questionKey === item.key &&
                                l.subFormId === opt.subForm.id,
                        )
                    ) {
                        loads.push({
                            questionKey: item.key,
                            subFormId: opt.subForm.id,
                            isMulti: item.controlType === 'selection-list',
                        });
                    }
                });
            });
        });

        return loads;
    }

    private isSubFormKeyMatch(itemKey: string, templateKey: string): boolean {
        return (
            !!itemKey &&
            (itemKey === templateKey ||
                itemKey.endsWith('__' + templateKey) ||
                itemKey.startsWith(templateKey + '__#'))
        );
    }

    private flattenSubFormTemplateItems(subFormTemplate: any): any[] {
        let template: any = subFormTemplate;

        if (subFormTemplate?.content || subFormTemplate?.Content) {
            template =
                typeof (subFormTemplate.content || subFormTemplate.Content) === 'string'
                    ? JSON.parse(subFormTemplate.content || subFormTemplate.Content)
                    : subFormTemplate.content || subFormTemplate.Content;
        }

        return (template?.formPanels || template?.FormPanels || []).reduce(
            (items: any[], panel: any) =>
                items.concat(panel.items || panel.Items || []),
            [],
        );
    }

    private markSubFormLoads(
        template: any,
        loads: { questionKey: string; subFormId: number; isMulti: boolean }[],
        templateMap: Map<number, any>,
    ): void {
        if (!template?.formPanels) return;

        loads.forEach((load) => {
            const subFormTemplate = templateMap.get(load.subFormId);
            if (!subFormTemplate) return;

            const newSubFormKey = load.subFormId?.toString();

            let triggerPanel: any = null;
            let triggerIndex = -1;
            for (const panel of template.formPanels) {
                const index = (panel.items || []).findIndex(
                    (item: any) => item.key === load.questionKey,
                );
                if (index >= 0) {
                    triggerPanel = panel;
                    triggerIndex = index;
                    break;
                }
            }

            if (!triggerPanel || triggerIndex < 0) return;

            const flattenedSubFormItems = this.flattenSubFormTemplateItems(
                subFormTemplate,
            );
            if (flattenedSubFormItems.length === 0) return;

            flattenedSubFormItems.forEach((tItem: any) => {
                const templateKey = tItem.key;

                const existingIndex = triggerPanel.items.findIndex(
                    (item: any, idx: number) =>
                        idx > triggerIndex &&
                        this.isSubFormKeyMatch(item.key, templateKey) &&
                        !(
                            item._subFormItemMarker === this.subFormItemMarker &&
                            item._subFormId &&
                            item._subFormId !== newSubFormKey
                        ),
                );

                if (existingIndex >= 0) {
                    const existing = triggerPanel.items[existingIndex];
                    existing._subFormItemMarker = this.subFormItemMarker;
                    existing._subFormId = newSubFormKey;
                    existing._subFormQuestionKey = load.questionKey;
                    existing._isSubFormItem = true;
                    existing.readonly = true;
                }
            });

            const subFormItemIndices: number[] = [];
            triggerPanel.items.forEach((item: any, idx: number) => {
                if (
                    item._subFormItemMarker === this.subFormItemMarker &&
                    item._subFormQuestionKey === load.questionKey
                ) {
                    subFormItemIndices.push(idx);
                }
            });
            subFormItemIndices.forEach((idx, i) => {
                triggerPanel.items[idx]._isFirstSubFormItem = i === 0;
                triggerPanel.items[idx]._isLastSubFormItem =
                    i === subFormItemIndices.length - 1;
            });
        });
    }

    onSubFormTriggered(event: any): void {
        this.subFormHandler.onSubFormTriggered(
            event,
            () => this.form,
            (result) => this.parseTemplate(result),
            {
                onFormUpdated: (updatedForm) => {
                    this.form = updatedForm;
                },
            },
        );
    }

    private getSourceFormByQuestionKey(questionKey: string): string {
        const panel = this.form?.formPanels?.find((formPanel: any) =>
            formPanel.items?.some((item: any) => item.key === questionKey),
        ) as any;

        return panel?._sourceForm || 'issuer';
    }

    submit(event: any, overrideRejected: boolean = false) {
        if (!event.email) {
            this.popupService.alert('raise.emailRequired');
            return;
        }

        if (
            this.overrideExisting &&
            !this.viewOnly &&
            this.originalTemplateId == null &&
            this.ecfFormTemplateId == null
        ) {
            this.popupService.alert('common.unknownError');
            return;
        }

        const email = event.email;
        const name = event.fullName || event.name || null;

        if (
            !this.submissionToken ||
            email.trim().toLowerCase() !== (this.email || '').trim().toLowerCase()
        ) {
            this.popupService.alert('common.unknownError');
            return;
        }

        // Keep panels under their original source form
        const issuerFormToSubmit = {
            ...JSON.parse(JSON.stringify(this.issuerFormTemplate)),
            formPanels: this.getPanelsBySourceForm('issuer'),
        };

        const ecfFormToSubmit = {
            ...JSON.parse(JSON.stringify(this.ecfFormTemplate)),
            formPanels: this.getPanelsBySourceForm('ecf'),
        };

        // Update values from event
        this.updateValues(issuerFormToSubmit, event);
        this.updateValues(ecfFormToSubmit, event);

        this.popupService
            .confirm('raise.confirmSubmitFormMessage', {
                titleLabel: 'common.confirmation',
                okLabel: 'raise.yesSubmit',
                cancelLabel: 'raise.noBackToEdit',
            } as PopupOptions)
            .then(
                () => {
                    this.blockUiService.open();

                    // Temporarily save form data before submitting.
                    this.pendingSubmission = {
                        dynamicFormTemplateId:
                            this.originalTemplateId ?? this.ecfFormTemplateId,
                        content: JSON.stringify(issuerFormToSubmit),
                        ecfContent: JSON.stringify(ecfFormToSubmit),
                        email: email,
                        name: name,
                        overrideRejected:
                            overrideRejected || this.overrideExisting,
                    };

                    this.email = email;

                    this.submitVerifiedApplication();
                },
                () => { },
            );
    }

    private getPanelsBySourceForm(sourceForm: string): any[] {
        return (this.form?.formPanels || [])
            .filter((panel: any) => panel._sourceForm === sourceForm)
            .map((panel: any) => {
                const clonedPanel = JSON.parse(JSON.stringify(panel));

                delete clonedPanel._sourceForm;

                clonedPanel.items = (clonedPanel.items || []).map((item: any) => {
                    delete item._originalKey;
                    delete item._subFormItemMarker;
                    delete item._subFormId;
                    delete item._subFormQuestionKey;
                    delete item._isSubFormItem;
                    delete item._isFirstSubFormItem;
                    delete item._isLastSubFormItem;
                    return item;
                });

                return clonedPanel;
            });
    }

    private updateValues(template: FormTemplate, event: any): void {
        template.formPanels?.forEach((formPanel: any) => {
            formPanel.items?.forEach((item: any) => {
                if (item.key && item.key in event) {
                    const eventValue = event[item.key];

                    if (eventValue == null) {
                        item.value = null;
                    } else if (item.controlType === 'datepicker') {
                        item.value = moment(eventValue).toISOString();
                    } else if (
                        item.type === 'array' ||
                        Array.isArray(eventValue) ||
                        typeof eventValue === 'object'
                    ) {
                        try {
                            item.value = JSON.stringify(eventValue);
                        } catch {
                            item.value = String(eventValue);
                        }
                    } else {
                        item.value = eventValue.toString();
                    }
                }

                if (Array.isArray(item.value)) {
                    item.value = JSON.stringify(item.value);
                }
            });
        });
    }

    private submitVerifiedApplication(
        overrideRejected?: boolean,
    ): void {
        if (!this.pendingSubmission || !this.submissionToken) {
            this.popupService.alert('common.unknownError');
            return;
        }

        const pendingSubmission = this.pendingSubmission;

        this.raiseService
            .submitUnregisteredApplication(
                this.submissionToken,
                pendingSubmission.dynamicFormTemplateId,
                pendingSubmission.content,
                pendingSubmission.email,
                pendingSubmission.name,
                overrideRejected ??
                pendingSubmission.overrideRejected,
                pendingSubmission.ecfContent,
            )
            .subscribe({
                next: () => {
                    this.blockUiService.close();
                    this.pendingSubmission = null;

                    this.popupService
                        .success('raise.submissionSuccessful')
                        .then(() => {
                            this.router.navigate(['/raise'], {
                                state: {
                                    showSubmittedApplicationPrompt: true,
                                    email: pendingSubmission.email,
                                    submissionToken: this.submissionToken,
                                    viewToken: this.viewToken
                                }
                            });
                        });
                },
                error: (err) => {
                    this.blockUiService.close();
                    this.handleVerifiedSubmissionError(err);
                },
            });
    }

    private handleVerifiedSubmissionError(err: any): void {
        let backendMessage = '';

        if (err?.Error?.Message) {
            backendMessage = err.Error.Message;
        } else if (err?.error?.Error?.Message) {
            backendMessage = err.error.Error.Message;
        } else if (err?.error?.message) {
            backendMessage = err.error.message;
        } else if (err?.ErrorMessage) {
            backendMessage = err.ErrorMessage;
        } else if (typeof err === 'string') {
            backendMessage = err;
        }

        if (backendMessage === 'ERR_USER_ALREADY_EXISTS') {
            this.popupService
                .alert('common.emailExisting')
                .then(() => {
                    this.router.navigate(['/login']);
                });

            return;
        }

        if (backendMessage === 'ERR_SUBMISSION_REJECTED') {
            this.popupService
                .confirm('common.emailRejected')
                .then(
                    () => {
                        this.submitVerifiedApplication(true);
                    },
                    () => { },
                );

            return;
        }

        this.errorService.showError(err);
    }

    back() {
        this.location.back();
    }
}
